// src/services/http.js

import axios from 'axios'
import { API_CONFIG, HTTP_STATUS } from './config'
import cookieService from './cookie'
import notification from './notification'

const axiosInstance = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
})

// Request interceptor
axiosInstance.interceptors.request.use(
    (config) => {
        const token = cookieService.getToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
         // Per DELETE, non inviamo body e non impostiamo Content-Type
        if (config.method === 'delete') {
            delete config.data
            delete config.headers?.['Content-Type']
            return config
        }
        
        // Se i dati sono un oggetto e non FormData, stringificali
        if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
            config.data = JSON.stringify(config.data)
            config.headers['Content-Type'] = 'application/json'
        }
        return config
    },
    (error) => Promise.reject(error)
)

// Response interceptor - usa notification service
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status
        const errorMessage = error.response?.data?.message || error.message

        if (status === HTTP_STATUS.UNAUTHORIZED) {
            cookieService.removeToken()
            const publicPaths = ['/login', '/register', '/board']
            const isPublicPath = publicPaths.some(path => window.location.pathname.includes(path))
            
            if (!isPublicPath && !window.location.pathname.includes('/public')) {
                notification.error('Sessione scaduta. Effettua nuovamente il login.')
                setTimeout(() => {
                    window.location.href = '/agency/login'
                }, 100)
            }
        } else if (status === HTTP_STATUS.FORBIDDEN) {
            notification.error(errorMessage || 'Accesso non autorizzato')
        } else if (status === HTTP_STATUS.NOT_FOUND) {
            notification.error('Risorsa non trovata')
        } else if (status === HTTP_STATUS.SERVER_ERROR) {
            notification.error('Errore del server. Riprova più tardi.')
        } else if (!status) {
            notification.error('Errore di connessione. Verifica la tua connessione internet.')
        }

        return Promise.reject(error)
    }
)

// Helper per download file
export const downloadFile = async (fileId, fileName) => {
    try {
        const response = await axiosInstance.get(`/api/public/files/${fileId}`, {
            responseType: 'blob',
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName || `file_${fileId}`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        notification.success('Download completato con successo')
        return { success: true }
    } catch (error) {
        console.error('Download error:', error)
        notification.error('Errore durante il download del file')
        return { success: false, error }
    }
}

export const getFilePreviewUrl = async (fileId) => {
    try {
        const response = await axiosInstance.get(`/api/public/files/${fileId}`, {
            responseType: 'blob',
        })
        return URL.createObjectURL(response.data)
    } catch (error) {
        console.error('Preview error:', error)
        return null
    }
}

export default axiosInstance